import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { disqualifyExamSession } from "../../../../services/examSessionsApi";

export const useExamDisqualify = (examInfo, user, setError, enabled) => {
  const { t } = useTranslation();
  const [disqualified, setDisqualified] = useState(false);
  const outOfFocusStartRef = useRef(null);
  const disqualifyInProgressRef = useRef(false);

  const handleDisqualify = useCallback(
    async (secondsOutOfFocus) => {
      if (!examInfo?.examId) return;
      if (!user?.studentNumber) return;
      if (disqualified) return;
      if (disqualifyInProgressRef.current) return;

      disqualifyInProgressRef.current = true;

      try {
        await disqualifyExamSession({
          examId: examInfo.examId,
          studentNumber: user.studentNumber,
          reason: "TAB_CHANGED_TOO_LONG",
          secondsOutOfFocus,
        });
        setDisqualified(true);
        setError(t("pages.examSession.disqualifiedDueToTabSwitch"));
      } catch (e) {
        const msg = e?.response?.data?.message || t("pages.examSession.disqualifyError");
        setError(msg);
      } finally {
        disqualifyInProgressRef.current = false;
      }
    },
    [examInfo, user, disqualified, setError, t]
  );

  useEffect(() => {
    if (!enabled) return;
    if (disqualified) return;
    if (!examInfo?.examId) return;
    if (!user?.studentNumber) return;

    const thresholdSeconds = 5;

    const finishOutOfFocus = () => {
      if (!outOfFocusStartRef.current) return;
      const diffMs = Date.now() - outOfFocusStartRef.current;
      outOfFocusStartRef.current = null;
      const seconds = Math.floor(diffMs / 1000);
      if (seconds >= thresholdSeconds) {
        handleDisqualify(seconds);
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (!outOfFocusStartRef.current) outOfFocusStartRef.current = Date.now();
      } else {
        finishOutOfFocus();
      }
    };

    const handleBlur = () => {
      if (!outOfFocusStartRef.current) outOfFocusStartRef.current = Date.now();
    };

    const handleFocus = () => {
      finishOutOfFocus();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleBlur);
    window.addEventListener("focus", handleFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleBlur);
      window.removeEventListener("focus", handleFocus);
    };
  }, [examInfo, user, handleDisqualify, enabled, disqualified]);

  return { disqualified };
};
